import { Button } from "@dash/form";
import { DocsSelectDialog } from "@dash/select";
import { useTheme } from "@emotion/react";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { MdAdd } from "react-icons/md";

import { CustomerDoc, isDocOfType } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

import { CrmCustomerCard } from "../crm/CrmCustomerCard";
import { SupportFieldLabel } from "../shared/SupportFieldLabel";

type Props = {
  assignedCustomerUuids: Uuid[];
  onAddCustomer: (customerUuid: Uuid) => void;
  onRemoveCustomer: (customerUuid: Uuid) => void;
};

export const SupportCreateTicketAssignedCustomersField: FC<Props> = ({
  assignedCustomerUuids,
  onAddCustomer,
  onRemoveCustomer,
}) => {
  const theme = useTheme();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const docsRetrieveQuery = useClientManager("docs")
    .retrieveDocs()
    .useQuery(
      { docIds: assignedCustomerUuids.map((uuid) => `customer:${uuid}`) },
      { enabled: assignedCustomerUuids.length > 0 },
    );

  const assignedCustomers = useMemo((): CustomerDoc[] => {
    return (docsRetrieveQuery.data ?? []).filter(
      (doc): doc is CustomerDoc => doc !== null && isDocOfType("customer")(doc),
    );
  }, [docsRetrieveQuery.data]);

  const hasAssignedCustomers = assignedCustomerUuids.length > 0;

  const openAssignDialog = () => setIsAddDialogOpen(true);

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
      <SupportFieldLabel>Przypisani klienci</SupportFieldLabel>
      {hasAssignedCustomers ? (
        docsRetrieveQuery.isLoading ? (
          <div
            css={{
              padding: "0.75rem 1rem",
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.smallBorderRadius,
              fontSize: "14px",
              color: theme.colors.secondaryText,
            }}
          >
            Ładowanie klientów...
          </div>
        ) : (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {assignedCustomers.map((customer) => (
              <CrmCustomerCard
                key={customer.uuid}
                customer={customer}
                caption="Przypisany klient"
                onDetach={() => onRemoveCustomer(customer.uuid)}
              />
            ))}
          </div>
        )
      ) : null}
      <Button
        variant="outline"
        size="small"
        icon={<MdAdd size={16} />}
        onClick={openAssignDialog}
      >
        {hasAssignedCustomers ? "Przypisz" : "Przypisz klienta z CRM"}
      </Button>
      <DocsSelectDialog
        docType={["customer"]}
        variant="single-select"
        label="Przypisz klienta z CRM"
        noTrigger
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSelect={(option) => {
          if (option && isDocOfType("customer")(option) && option.value) {
            onAddCustomer(Uuid(option.value));
          }
          setIsAddDialogOpen(false);
        }}
      />
    </div>
  );
};
